import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

// const RETAILER_TYPES = ["online", "in-store"];

export default function RetailerFormModal({
  open,
  onClose,
  onSubmit,
  initialData,
  existingRetailers = [],
  countryCodes = [],
}) {
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      retailer_id: "",
      retailer_name: "",
      display_text: "",
      country_code: "",
      // retailer_type: "",
    },
  });

  const watchedName = useWatch({
    control,
    name: "retailer_name",
    defaultValue: "",
  });
  const watchedCountryCode = useWatch({
    control,
    name: "country_code",
    defaultValue: "",
  });
  // const watchedRetailerType = useWatch({
  //   control,
  //   name: "retailer_type",
  //   defaultValue: "",
  // });
  const watchedRetailerId = useWatch({
    control,
    name: "retailer_id",
    defaultValue: "",
  });

  const isSimilarNameAvailable = existingRetailers.some((retailer) => {
    if (initialData && retailer.retailer_id === initialData.retailer_id) {
      return false;
    }

    const currentName = watchedName.toLowerCase().trim();
    const currentCountryCode = watchedCountryCode.toLowerCase().trim();
    // const currentRetailerType = watchedRetailerType.toLowerCase().trim();

    const existingName = (retailer.retailer_name || "").toLowerCase().trim();
    const existingCountryCode = (retailer.country_code || "")
      .toLowerCase()
      .trim();
    // const existingRetailerType = (retailer.retailer_type || "")
    //   .toLowerCase()
    //   .trim();

    if (!currentName || !existingName) return false;

    const isSameCountry =
      currentCountryCode && existingCountryCode
        ? currentCountryCode === existingCountryCode
        : false;
    // const isSameType =
    //   currentRetailerType && existingRetailerType
    //     ? currentRetailerType === existingRetailerType
    //     : true;

    const isNameSimilar =
      existingName === currentName ||
      existingName.includes(currentName) ||
      currentName.includes(existingName);

    // return isSameCountry && isSameType && isNameSimilar;
    return isSameCountry && isNameSimilar;
  });

  useEffect(() => {
    if (initialData) {
      reset({
        retailer_id: initialData.retailer_id || "",
        retailer_name: initialData.retailer_name || "",
        display_text: initialData.display_text || "",
        country_code: initialData.country_code || "",
        // retailer_type: initialData.retailer_type || "",
      });
    } else {
      reset({
        retailer_id: "",
        retailer_name: "",
        display_text: "",
        country_code: "",
        // retailer_type: "",
      });
    }
  }, [initialData, reset, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Retailer" : "Add New Retailer"}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Wanrning Alert */}
            {isSimilarNameAvailable && (
              <Alert severity="warning">
                Note: A retailer with a similar name is already registred under
                this <strong> country code </strong>.
              </Alert>
            )}

            {/* Retailer Id - Disabled */}
            {watchedRetailerId && (
              <Controller
                name="retailer_id"
                control={control}
                rules={{ required: "Retailer id is required during edit!" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Retailer ID"
                    fullWidth
                    error={Boolean(errors.retailer_id)}
                    helperText={errors.retailer_id?.message}
                    disabled
                  />
                )}
              />
            )}

            {/* Retailer Name - Forced Uppercase */}
            <Controller
              name="retailer_name"
              control={control}
              rules={{ required: "Retailer name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Retailer Name"
                  fullWidth
                  error={Boolean(errors.retailer_name)}
                  helperText={errors.retailer_name?.message}
                  // Intercept change to convert to uppercase immediately in state
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />
            {/* Display Text - Forced Uppercase */}
            <Controller
              name="display_text"
              control={control}
              rules={{ required: "Display Text is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Display Text"
                  fullWidth
                  error={Boolean(errors.display_text)}
                  helperText={errors.display_text?.message}
                  // Intercept change to convert to uppercase immediately in state
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />

            {/* Country Code Select */}
            <Controller
              name="country_code"
              control={control}
              rules={{ required: "Country Code is required" }}
              render={({ field }) => (
                <FormControl fullWidth error={Boolean(errors.country_code)}>
                  <InputLabel id="country-code-label">Country Code</InputLabel>
                  <Select
                    {...field}
                    labelId="country-code-label"
                    label="Country Code"
                  >
                    {countryCodes.map((item, index) => {
                      const codeValue = item.country_code;
                      const labelText = item.country_name;

                      return (
                        <MenuItem key={codeValue + index} value={codeValue}>
                          {`${codeValue} - ${labelText}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.country_code && (
                    <FormHelperText>
                      {errors.country_code?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {/* Retailer Type Select */}
            {/* 
            <Controller
              name="retailer_type"
              control={control}
              rules={{ required: "Retailer type is required" }}
              render={({ field }) => (
                <FormControl fullWidth error={Boolean(errors.retailer_type)}>
                  <InputLabel id="retailer-type-label">
                    Retailer Type
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="retailer-type-label"
                    label="Retailer Type"
                  >
                    {RETAILER_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.retailer_type && (
                    <FormHelperText>
                      {errors.retailer_type?.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
             */}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {initialData ? "Save Changes" : "Add Retailer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
